import { useCallback, useEffect, useState } from "react";
import {
  ShieldAlert,
  Loader2,
  X,
  Check,
  LogOut,
  Download,
  Activity,
  Settings2,
} from "lucide-react";
import { API, formatApiErrorDetail } from "../auth/api";

function timeAgo(iso) {
  const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (secs < 60) return "just now";
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

const KIND_COPY = {
  bulk_read_volume: {
    title: "Unusual contact-list volume",
    body: (d) =>
      `Pulled the full contact list ${d.pulls} times in ${d.window_minutes} minutes (${d.records_per_pull} records each).`,
  },
  backup_download: {
    title: "Full backup downloaded",
    body: (d) => `Exported a complete database snapshot (${d.records} records).`,
  },
};

export default function SecurityDialog({ open, onClose }) {
  const [alerts, setAlerts] = useState([]);
  const [byActor, setByActor] = useState([]);
  const [settings, setSettings] = useState(null);
  const [showAcked, setShowAcked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s, l] = await Promise.all([
        API.get(`/security/alerts?include_acknowledged=${showAcked}`),
        API.get("/security/summary"),
        API.get("/security/access-log?hours=24"),
      ]);
      setAlerts(a.data);
      setSettings(s.data.settings);
      setByActor(l.data.by_actor);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [showAcked]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const act = async (id, path) => {
    setBusyId(id);
    setError("");
    try {
      await API.post(`/security/alerts/${id}/${path}`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const saveSetting = async (patch) => {
    try {
      const { data } = await API.put("/security/settings", patch);
      setSettings(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-start justify-center bg-black/70 backdrop-blur-sm p-6 overflow-auto"
      data-testid="security-dialog"
      role="dialog"
      aria-modal="true"
      aria-label="Export alerts"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >      <div className="w-full max-w-3xl border border-white/10 bg-[#0a0a0c] my-8">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-3.5 h-3.5 text-amber-300" />
            <span className="font-mono text-[11px] tracking-[0.22em] uppercase text-zinc-200">
              Export Alerts
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="security-dialog-close"
            className="text-zinc-500 hover:text-zinc-200 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div
            className="mx-5 mt-4 border border-rose-400/30 bg-rose-400/5 px-3 py-2 font-mono text-[11px] text-rose-200"
            data-testid="security-error"
          >
            {error}
          </div>
        )}

        {settings && (
          <div className="px-5 py-3 border-b border-white/10 flex flex-wrap items-center gap-3">
            <span className="flex items-center gap-1.5 font-mono text-[9px] tracking-[0.22em] uppercase text-zinc-500">
              <Settings2 className="w-3 h-3" /> Flag above
            </span>
            <input
              type="number"
              min="1"
              value={settings.list_pull_threshold}
              onChange={(e) =>
                setSettings({ ...settings, list_pull_threshold: Number(e.target.value) })
              }
              onBlur={(e) => saveSetting({ list_pull_threshold: Number(e.target.value) })}
              data-testid="threshold-input"
              className="w-16 bg-[#09090B] border border-white/10 focus:border-emerald-400 outline-none px-2 py-1 font-mono text-[12px] text-zinc-100"
            />
            <span className="font-mono text-[10px] text-zinc-500">list pulls in</span>
            <input
              type="number"
              min="1"
              value={settings.window_minutes}
              onChange={(e) => setSettings({ ...settings, window_minutes: Number(e.target.value) })}
              onBlur={(e) => saveSetting({ window_minutes: Number(e.target.value) })}
              data-testid="window-input"
              className="w-16 bg-[#09090B] border border-white/10 focus:border-emerald-400 outline-none px-2 py-1 font-mono text-[12px] text-zinc-100"
            />
            <span className="font-mono text-[10px] text-zinc-500">minutes</span>
            <label className="flex items-center gap-2 font-mono text-[10px] text-zinc-400 ml-auto">
              <input
                type="checkbox"
                checked={settings.alert_on_backup}
                onChange={(e) => saveSetting({ alert_on_backup: e.target.checked })}
                data-testid="alert-on-backup"
              />
              Flag every backup download
            </label>
          </div>
        )}

        <div className="divide-y divide-white/5" data-testid="alerts-list">
          {loading ? (
            <div className="px-5 py-6 flex items-center gap-2 font-mono text-[11px] text-zinc-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading alerts…
            </div>
          ) : alerts.length === 0 ? (
            <div
              className="px-5 py-8 font-mono text-[11px] text-zinc-500 text-center"
              data-testid="alerts-empty"
            >
              Nothing unusual. Bulk reads and backups are being monitored.
            </div>
          ) : (
            alerts.map((a) => {
              const copy = KIND_COPY[a.kind] || { title: a.kind, body: () => "" };
              return (
                <div key={a.id} className="px-5 py-4" data-testid={`alert-row-${a.id}`}>
                  <div className="flex items-start gap-3 flex-wrap">
                    <span
                      className={`mt-0.5 font-mono text-[9px] tracking-[0.16em] uppercase px-1.5 py-0.5 border ${
                        a.severity === "high"
                          ? "text-rose-300 border-rose-400/50"
                          : "text-amber-300 border-amber-400/40"
                      }`}
                    >
                      {a.severity}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] text-zinc-100">{copy.title}</div>
                      <div className="text-[12px] text-zinc-400 leading-relaxed mt-0.5">
                        {copy.body(a.detail || {})}
                      </div>
                      <div className="font-mono text-[10px] text-zinc-500 mt-1">
                        {a.actor_name} · {a.ip} · {timeAgo(a.last_seen_at)}
                        {a.occurrences > 1 ? ` · ${a.occurrences}x` : ""}
                        {a.acknowledged_at ? ` · cleared by ${a.acknowledged_by}` : ""}
                      </div>
                    </div>
                    {!a.acknowledged_at && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          disabled={busyId === a.id}
                          onClick={() => act(a.id, "revoke-sessions")}
                          data-testid={`revoke-actor-${a.id}`}
                          className="flex items-center gap-1.5 px-2.5 py-1 border border-rose-400/40 hover:bg-rose-400/10 disabled:opacity-40 font-mono text-[9px] tracking-[0.16em] uppercase text-rose-200 transition-colors"
                        >
                          <LogOut className="w-3 h-3" /> Sign Them Out
                        </button>
                        <button
                          type="button"
                          disabled={busyId === a.id}
                          onClick={() => act(a.id, "acknowledge")}
                          data-testid={`ack-alert-${a.id}`}
                          className="flex items-center gap-1.5 px-2.5 py-1 border border-white/10 hover:border-emerald-400/60 disabled:opacity-40 font-mono text-[9px] tracking-[0.16em] uppercase text-zinc-400 hover:text-emerald-300 transition-colors"
                        >
                          <Check className="w-3 h-3" /> Looks Fine
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="px-5 py-2 border-t border-white/10 flex items-center justify-between">
          <span className="flex items-center gap-1.5 font-mono text-[9px] tracking-[0.22em] uppercase text-zinc-500">
            <Activity className="w-3 h-3" /> Last 24 hours
          </span>
          <button
            type="button"
            onClick={() => setShowAcked((v) => !v)}
            data-testid="toggle-acknowledged"
            className="font-mono text-[9px] tracking-[0.18em] uppercase text-zinc-500 hover:text-cyan-300 transition-colors"
          >
            {showAcked ? "Hide cleared" : "Show cleared"}
          </button>
        </div>

        <div className="divide-y divide-white/5" data-testid="access-by-actor">
          {byActor.length === 0 ? (
            <div className="px-5 py-4 font-mono text-[10px] text-zinc-600">
              No bulk reads recorded yet.
            </div>
          ) : (
            byActor.map((r) => (
              <div
                key={r.actor_id}
                className="px-5 py-2.5 flex items-center gap-3 flex-wrap"
                data-testid={`access-row-${r.actor_id}`}
              >
                <span className="text-[12px] text-zinc-200 min-w-0 truncate flex-1">
                  {r.actor_name}
                </span>
                <span className="font-mono text-[10px] text-zinc-500">{r.pulls} list pulls</span>
                {r.backups > 0 && (
                  <span className="flex items-center gap-1 font-mono text-[10px] text-amber-300">
                    <Download className="w-3 h-3" /> {r.backups}
                  </span>
                )}
                <span className="font-mono text-[10px] text-zinc-600 truncate max-w-[14rem]">
                  {r.ips.join(", ")}
                </span>
                <span className="font-mono text-[10px] text-zinc-600">{timeAgo(r.last_at)}</span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
